import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import { aiConversationsAPI, AiConversation } from '../../services/api';
import {
  PlusIcon,
  ClockIcon,
  ChartIcon,
  MessageIcon,
  TrashLinesIcon,
  MessageDotsIcon,
} from '../icons';

// Icons
const Icons = {
  plus: <PlusIcon className="w-5 h-5" strokeWidth={2} />,
  clock: <ClockIcon className="w-4 h-4" strokeWidth={2} />,
  chart: <ChartIcon className="w-4 h-4" strokeWidth={2} />,
  message: <MessageIcon className="w-4 h-4" strokeWidth={2} />,
  trash: <TrashLinesIcon className="w-4 h-4" strokeWidth={2} />,
  empty: <MessageDotsIcon className="w-12 h-12" strokeWidth={1.5} />,
};

interface AiConversationListProps {
  selectedId?: number;
  onSelect: (conversation: AiConversation | null) => void;
  onNewChat: () => void;
}

const AiConversationList: React.FC<AiConversationListProps> = ({
  selectedId,
  onSelect,
  onNewChat,
}) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { isDark } = useTheme();
  const [conversations, setConversations] = useState<AiConversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      setIsLoading(true);
      const response = await aiConversationsAPI.getAll({ per_page: 50 });
      setConversations(response.data.conversations || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = await confirm({
      title: t('ai.conversations.deleteTitle'),
      message: t('ai.conversations.confirmDelete'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await aiConversationsAPI.delete(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (selectedId === id) {
        onSelect(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete conversation');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays === 0) {
      return t('ai.conversations.today');
    } else if (diffDays === 1) {
      return t('ai.conversations.yesterday');
    } else if (diffDays < 7) {
      return t('ai.conversations.daysAgo', { count: diffDays });
    } else {
      return date.toLocaleDateString();
    }
  };

  const getContextIcon = (contextType?: string) => {
    switch (contextType) {
      case 'time_entry_help':
        return Icons.clock;
      case 'time_entry_analysis':
        return Icons.chart;
      default:
        return Icons.message;
    }
  };

  return (
    <div
      className={`flex flex-col h-full rounded-2xl border ${
        isDark
          ? 'bg-slate-900/50 border-slate-700/50'
          : 'bg-white border-slate-200'
      }`}
    >
      {/* Header */}
      <div
        className={`p-4 border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
      >
        <button
          onClick={onNewChat}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-xl transition-all duration-200 shadow-lg shadow-violet-500/25 font-semibold"
        >
          {Icons.plus}
          <span>{t('ai.conversations.newChat')}</span>
        </button>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`h-16 rounded-xl animate-pulse ${
                  isDark ? 'bg-slate-700/50' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        ) : error ? (
          <div
            className={`p-4 text-center text-sm ${
              isDark ? 'text-rose-400' : 'text-rose-600'
            }`}
          >
            {error}
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-6 text-center">
            <div
              className={`mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`}
            >
              {Icons.empty}
            </div>
            <p
              className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
            >
              {t('ai.conversations.empty')}
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                onClick={() => onSelect(conversation)}
                className={`group flex items-start p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                  selectedId === conversation.id
                    ? isDark
                      ? 'bg-violet-500/20 border border-violet-500/30'
                      : 'bg-violet-50 border border-violet-200'
                    : isDark
                      ? 'hover:bg-slate-700/50'
                      : 'hover:bg-slate-50'
                }`}
              >
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                    selectedId === conversation.id
                      ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white'
                      : isDark
                        ? 'bg-slate-700 text-slate-400'
                        : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {getContextIcon(conversation.context_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-medium truncate ${
                      selectedId === conversation.id
                        ? isDark
                          ? 'text-violet-300'
                          : 'text-violet-900'
                        : isDark
                          ? 'text-slate-200'
                          : 'text-slate-900'
                    }`}
                  >
                    {conversation.title || t('ai.conversations.untitled')}
                  </p>
                  <div className="flex items-center mt-1 gap-2">
                    <span
                      className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                    >
                      {conversation.last_message_at
                        ? formatDate(conversation.last_message_at)
                        : formatDate(conversation.created_at)}
                    </span>
                    <span
                      className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-400'}`}
                    >
                      {conversation.message_count}{' '}
                      {t('ai.conversations.messages')}
                    </span>
                  </div>
                </div>
                <button
                  onClick={(e) => handleDelete(conversation.id, e)}
                  className={`flex-shrink-0 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 ${
                    isDark
                      ? 'text-slate-500 hover:text-rose-400 hover:bg-rose-500/10'
                      : 'text-slate-400 hover:text-rose-600 hover:bg-rose-50'
                  }`}
                >
                  {Icons.trash}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiConversationList;
